import { useState, useEffect, useRef } from 'react';
import _ from 'underscore';

// Debounced draft persistence over a LocalAPI-style store, shared by
// useMemoDraft and useTranscriptDraft. `store` is { load, save, clear },
// each taking the draftKey (and the value, for save). The store object and
// its functions must be stable across renders — define them at module scope.
//
// A pending debounced save is FLUSHED, not dropped, when the component
// unmounts or the key changes, so the newest keystrokes survive collapse /
// navigation within the debounce window.
export function useDraft(draftKey, store) {
  const [value, setValueState] = useState(() => store.load(draftKey));
  const valueRef = useRef(value);
  const debouncedSaveRef = useRef(null);
  const pendingRef = useRef(false);
  const keyRef = useRef(draftKey);

  // State-only setter; storage is untouched (used for optimistic UI moves
  // like clearing the field on submit while the draft stays for crash safety)
  const setValue = (newValue) => {
    valueRef.current = newValue;
    setValueState(newValue);
  };

  // Reload the stored draft when the key changes. The initial load is the
  // lazy useState initializer, so skip the mount run.
  useEffect(() => {
    if (keyRef.current === draftKey) return;
    keyRef.current = draftKey;
    setValue(store.load(draftKey));
  }, [draftKey]);

  useEffect(() => {
    const debounced = _.debounce((newValue) => {
      pendingRef.current = false;
      store.save(draftKey, newValue);
    }, 500);
    debouncedSaveRef.current = debounced;

    return () => {
      debounced.cancel();
      // Flush, don't drop: a save scheduled within the last 500ms must still
      // land, or unmounting mid-typing silently loses the newest keystrokes
      if (pendingRef.current) {
        pendingRef.current = false;
        store.save(draftKey, valueRef.current);
      }
    };
  }, [draftKey]);

  // Update state and persist the draft (debounced)
  const update = (newValue) => {
    setValue(newValue);
    pendingRef.current = true;
    debouncedSaveRef.current?.(newValue);
  };

  // Persist a specific value immediately, e.g. before a submit so the text
  // survives a crash mid-request
  const persistNow = (newValue) => {
    debouncedSaveRef.current?.cancel();
    pendingRef.current = false;
    store.save(draftKey, newValue);
  };

  // Persist whatever the field currently holds. After a successful submit
  // this clears the draft if the field is empty, but keeps text the user
  // typed while the request was in flight.
  const syncNow = () => {
    persistNow(valueRef.current);
  };

  const clearDraft = () => {
    debouncedSaveRef.current?.cancel();
    pendingRef.current = false;
    store.clear(draftKey);
  };

  return {
    value,
    setValue,
    update,
    persistNow,
    syncNow,
    clearDraft
  };
}

export default useDraft;
